import React from 'react';
import {
  TouchableOpacity,
  TouchableOpacityProps,
  ActivityIndicator,
  ViewStyle,
} from 'react-native';
import styled from 'styled-components/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { Theme, getElevationStyle } from '@theme/aiaTheme';
import { useTheme } from '@theme/ThemeProvider';
import { Text } from './Text';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<TouchableOpacityProps, 'style'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  iconLeft?: string;
  iconRight?: string;
  children: React.ReactNode;
  style?: ViewStyle;
}

interface StyledButtonProps {
  variant: ButtonVariant;
  size: ButtonSize;
  fullWidth: boolean;
  disabled: boolean;
}

const StyledButton = styled(TouchableOpacity)<StyledButtonProps>`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.md}px;
  padding-horizontal: ${({ theme, size }) =>
    size === 'sm' ? theme.spacing.base : size === 'lg' ? theme.spacing.xl : theme.spacing.lg}px;
  padding-vertical: ${({ theme, size }) =>
    size === 'sm' ? theme.spacing.sm : size === 'lg' ? theme.spacing.base : theme.spacing.md}px;
  min-height: ${({ theme, size }) =>
    size === 'sm' ? 36 : size === 'lg' ? theme.minTouchTarget + 8 : theme.minTouchTarget}px;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  background-color: ${({ theme, variant, disabled }) => {
    if (disabled) return theme.colors.neutral300;
    switch (variant) {
      case 'primary':
        return theme.colors.brandPrimary;
      case 'secondary':
        return theme.colors.brandSecondary;
      case 'tertiary':
        return theme.colors.surface;
      case 'ghost':
        return theme.colors.transparent;
      default:
        return theme.colors.brandPrimary;
    }
  }};
  border-width: ${({ variant }) => (variant === 'tertiary' ? 1 : 0)}px;
  border-color: ${({ theme, variant }) =>
    variant === 'tertiary' ? theme.colors.border : theme.colors.transparent};
  ${({ theme, variant }) =>
    variant === 'tertiary' || variant === 'ghost' ? '' : getElevationStyle(theme.elevation.sm)};
`;

const ButtonText = styled.Text<{
  variant: ButtonVariant;
  size: ButtonSize;
  disabled: boolean;
}>`
  color: ${({ theme, variant, disabled }) => {
    if (disabled) return theme.colors.textDisabled;
    if (variant === 'primary' || variant === 'secondary') return theme.colors.textOnPrimary;
    return theme.colors.textPrimary;
  }};
  font-size: ${({ theme, size }) =>
    size === 'sm' ? theme.typography.buttonSmall.fontSize : theme.typography.button.fontSize}px;
  line-height: ${({ theme, size }) =>
    size === 'sm' ? theme.typography.buttonSmall.lineHeight : theme.typography.button.lineHeight}px;
  font-weight: ${({ theme }) => theme.typography.button.fontWeight};
  margin-horizontal: ${({ theme }) => theme.spacing.xs}px;
`;

const IconWrapper = styled.View<{ position: 'left' | 'right' }>`
  margin-${({ position }) => (position === 'left' ? 'right' : 'left')}: ${({ theme }) =>
    theme.spacing.xs}px;
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  iconLeft,
  iconRight,
  children,
  style,
  ...props
}) => {
  const theme = useTheme();
  const isDisabled = disabled || loading;
  const iconSize = size === 'sm' ? 18 : size === 'lg' ? 24 : 20;
  const iconColor =
    variant === 'primary' || variant === 'secondary'
      ? theme.colors.textOnPrimary
      : isDisabled
        ? theme.colors.textDisabled
        : theme.colors.textPrimary;

  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={isDisabled}
      activeOpacity={0.7}
      style={style}
      {...props}
    >
      {loading ? (
        <ActivityIndicator
          size={size === 'sm' ? 'small' : 'small'}
          color={
            variant === 'primary' || variant === 'secondary'
              ? theme.colors.textOnPrimary
              : theme.colors.brandPrimary
          }
        />
      ) : (
        <>
          {iconLeft && (
            <IconWrapper position="left">
              <Icon name={iconLeft} size={iconSize} color={iconColor} />
            </IconWrapper>
          )}
          <ButtonText variant={variant} size={size} disabled={isDisabled}>
            {children}
          </ButtonText>
          {iconRight && (
            <IconWrapper position="right">
              <Icon name={iconRight} size={iconSize} color={iconColor} />
            </IconWrapper>
          )}
        </>
      )}
    </StyledButton>
  );
};
